import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { HTTPService } from './http.service';
import { DashBoardInterface } from '../system-view/main-dashboard/interfaces/dashboard';
import { serializeDashboard } from '../system-view/main-dashboard/dashboard-serializer';
import { FormGroup } from '@angular/forms';
import moment from 'moment';
import { DatePipe } from '@angular/common';
import { UserConfigServices } from './user-config.services';
import { NzMessageService } from 'ng-zorro-antd/message';

export interface SaveDashboardResult {
  INTERNALID: number;
}

/**
 * Persistência do dashboard (entidade Mktpdashboard) + preferências
 * de usuário (Mktpuserpref) + contexto de filtros ({@PARAM_*}).
 *
 * ATENÇÃO — estado por dashboard: este service guarda codeCompany/
 * codeContract/name do dashboard corrente; ele é provido NO COMPONENTE
 * main-dashboard (uma instância por dashboard vivo), nunca como
 * singleton, para dois dashboards abertos não corromperem o contexto
 * um do outro.
 */
@Injectable()
export class DashboardViewService {
  name: string;

  addDefaultFilters(filters: string[]) {
    filters.push("PARAM_CODECOMPANY");
    filters.push("PARAM_CODECOMPANY_LIST");
    filters.push("PARAM_CODECONTRACT");
    filters.push("PARAM_CODECONTRACT_LIST");
    filters.push("PARAM_IDUSER");
  }

  private _codecompany_list: string;
  private _codecontractlist: string;
  private _codecontract: number;
  private _codecompany: number;
  private _iduser: number;

  set codeContract(codecontract: number) {
    this._codecontract = codecontract;
  }
  set codeCompany(codecompany: number) {
    this._codecompany = codecompany;
  }
  set idUser(iduser: number) {
    this._iduser = iduser;
  }

  set codeCompanyList(codecompanylist: string) {
    this._codecompany_list = codecompanylist;
  }

  set codeContractList(codecontractlist: string) {
    this._codecontractlist = codecontractlist;
  }

  get codeCompanyList() {
    return this._codecompany_list;
  }

  get codeContractList() {
    return this._codecontractlist;
  }

  get idUser() {
    return this._iduser;
  }

  get codeCompany() {
    return this._codecompany;
  }

  get codeContract() {
    return this._codecontract;
  }

  applyFilters(query: string, form: FormGroup<any>): string {
    let newQuery = query;

    let reg: RegExpMatchArray = query.match(/{@(.*?)}/g);
    if (reg != null) {
      reg.forEach(d => {

        let fieldName = d.replace("{@", "").replace("}", "").trim();

        if (fieldName.toUpperCase() == "PARAM_CODECOMPANY") {
          newQuery = newQuery.replace(d, String(this.codeCompany ?? 'NULL'));
        }

        if (fieldName.toUpperCase() == "PARAM_CODECOMPANY_LIST") {
          newQuery = newQuery.replace(d, String(this.codeCompanyList ?? 'NULL'));
        }

        if (fieldName.toUpperCase() == "PARAM_CODECONTRACT_LIST") {
          newQuery = newQuery.replace(d, String(this.codeContractList ?? 'NULL'));
        }

        if (fieldName.toUpperCase() == "PARAM_CODECONTRACT") {
          newQuery = newQuery.replace(d, String(this.codeContract ?? 'NULL'));
        }

        if (fieldName.toUpperCase() == "PARAM_IDUSER") {
          newQuery = newQuery.replace(d, String(this.idUser ?? 'NULL'));
        }

        if (form != null) {
          if (form.get(fieldName) != null) {
            let value = form.get(fieldName).value;

            if (value != null) {
              if (value instanceof moment) {
                let newData = moment(value).format('yyyy-MM-DD HH:mm:ss.SSS');
                newQuery = newQuery.replace(d, newData);
              } else {
                newQuery = newQuery.replace(d, form.get(fieldName).value);
              }
            }
          }
        }
      });
    }
    return newQuery;
  }

  async saveUserConfig(
    iduser: number,
    resourceId: string,
    preference: object): Promise<unknown> {
    try {
      return await this.ucs.saveUserResource(resourceId, iduser, preference);
    } catch (err) {
      this.message.error('Falha ao salvar a preferência do usuário.');
      throw err;
    }
  }

  async removeDashBoardUserPreference(idresource: string): Promise<unknown> {
    const ob = {
      "entityName": "Mktpuserpref",
      "condition": `idpreference='${idresource}'`
    };
    try {
      return await firstValueFrom(this.http.post(ob, 'JsonBrokerServices/remove', false, true, false));
    } catch (err) {
      this.message.error('Falha ao remover a preferência do usuário.');
      throw err;
    }
  }

  /**
   * Retorna a PREFERENCE serializada do recurso, ou null quando não há.
   */
  async loadPreference(iduser: number, resourceId: string): Promise<string | null> {
    const data = await firstValueFrom(this.ucs.loadUserResource(iduser, resourceId));
    const rows: Record<string, unknown>[] = data?.['Mktpuserpref'] ?? [];
    const preference = rows.length > 0 ? rows[0]['PREFERENCE'] : null;
    return typeof preference === 'string' && preference.length > 0 ? preference : null;
  }

  /** @deprecated usar loadPreference() — mantido até a migração dos gadgets */
  loadDashBoardUserPreference(iduser: number, resourceId: string) {
    return new Promise((resolve, reject) => {
      this.ucs.loadUserResource(iduser, resourceId).subscribe({
        next: (data) => {
          let val: Object[] = data['Mktpuserpref'];
          if (val != null && val.length > 0) {
            resolve(val[0]);
          } else {
            resolve([]);
          }
        },
        error: (erro) => {
          reject(erro);
        }
      });
    })
  }

  loadData(query: string) {

    let ob = {
      "entityName": "Mktpdashboard",
      "sql": query
    }

    return this.http.post(ob, 'JsonBrokerServices/findnative', false, true, false, false);

  }

  private _clicked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private http: HTTPService, public datepipe: DatePipe,
    private ucs: UserConfigServices,
    private message: NzMessageService
  ) { }

  notifyClick(newData: boolean) {
    this._clicked.next(newData);
  }

  get clicked(): BehaviorSubject<boolean> {
    return this._clicked;
  }

  /**
   * Carrega a configuração crua persistida. A normalização (defaults de
   * gridster, limpeza de lixo legado) é feita pelo componente via
   * normalizeDashboard(), que possui a factory das options default.
   */
  getDashBoardConfiguration(id: number): Observable<DashBoardInterface[]> {

    let ob = {
      "entityName": "Mktpdashboard",
      "sql": `SELECT * FROM MKTPDASHBOARD WHERE INTERNALID=${id}`
    }

    return this.http.post(ob, 'JsonBrokerServices/findnative', false, true, false).pipe(map(value => {
      const rows = value?.['SQL'] as Record<string, unknown>[] | undefined;
      if (rows == null || rows.length === 0) {
        return [];
      }
      this.codeCompany = rows[0]['CODECOMPANY'] as number;
      this.codeContract = rows[0]['CODECONTRACT'] as number;
      this.name = rows[0]['NAME'] as string;
      try {
        const parsed = JSON.parse(rows[0]['EXPRESSION'] as string);
        return Array.isArray(parsed) ? parsed as DashBoardInterface[] : [];
      } catch {
        this.message.error('A configuração salva deste dashboard é inválida.');
        return [];
      }
    }));
  }

  /**
   * Grava o dashboard. REJEITA em erro (com toast) — o chamador decide
   * rollback. O modelo passa por serializeDashboard(): nenhum estado
   * runtime (options.api, chart.options.data, active) vai para o banco.
   */
  async saveDashBoard(id: number, dashboard: DashBoardInterface[]): Promise<SaveDashboardResult> {
    const ob = {
      "entityName": "Mktpdashboard",
      "INTERNALID": id,
      "CODECOMPANY": this.codeCompany,
      "CODECONTRACT": this.codeContract,
      "NAME": this.name,
      "EXPRESSION": JSON.stringify(serializeDashboard(dashboard))
    };
    try {
      const data = await firstValueFrom(this.http.post(ob, 'JsonBrokerServices/save', false, true, false));
      return data as SaveDashboardResult;
    } catch (err) {
      this.message.error('Falha ao salvar o dashboard. As alterações NÃO foram gravadas.');
      throw err;
    }
  }

}
